import React from 'react';

const buildPdfSrc = (structuredData) => {
    const url = typeof structuredData?.url === 'string' ? structuredData.url.trim() : '';
    if (url) {
        return url;
    }

    const base64 = typeof structuredData?.base64 === 'string'
        ? structuredData.base64.trim()
        : typeof structuredData?.pdfBase64 === 'string' ? structuredData.pdfBase64.trim() : '';

    if (!base64) {
        return '';
    }

    return `data:application/pdf;base64,${base64}`;
};

const toDownloadName = (value, fallback) => {
    const normalized = typeof value === 'string' ? value.trim() : '';
    if (!normalized) {
        return fallback;
    }

    return normalized
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-_]/g, '') || fallback;
};

const getFileNameFromUrl = (url) => {
    if (typeof url !== 'string' || url.trim().length === 0) {
        return '';
    }

    try {
        const parsedUrl = new URL(url, window.location.origin);
        const pathParts = parsedUrl.pathname.split('/').filter(Boolean);
        return pathParts.length > 0 ? decodeURIComponent(pathParts[pathParts.length - 1]) : '';
    } catch (error) {
        return '';
    }
};

const toHumanSize = (bytes) => {
    const normalizedBytes = Number(bytes);
    if (!Number.isFinite(normalizedBytes) || normalizedBytes <= 0) {
        return '';
    }

    const thresholds = [
        { unit: 'GB', value: 1024 * 1024 * 1024 },
        { unit: 'MB', value: 1024 * 1024 },
        { unit: 'KB', value: 1024 }
    ];

    const threshold = thresholds.find((entry) => normalizedBytes >= entry.value);
    if (!threshold) {
        return `${normalizedBytes} B`;
    }

    const scaledValue = normalizedBytes / threshold.value;
    const precision = scaledValue >= 10 ? 1 : 2;
    return `${scaledValue.toFixed(precision)} ${threshold.unit}`;
};

const inferPdfSize = (structuredData) => {
    const explicitSizeLabel = typeof structuredData?.sizeLabel === 'string' ? structuredData.sizeLabel.trim() : '';
    if (explicitSizeLabel) {
        return explicitSizeLabel;
    }

    const explicitSizeBytes = structuredData?.sizeBytes || structuredData?.fileSizeBytes;
    const explicitSize = toHumanSize(explicitSizeBytes);
    if (explicitSize) {
        return explicitSize;
    }

    const base64 = typeof structuredData?.base64 === 'string'
        ? structuredData.base64.trim()
        : typeof structuredData?.pdfBase64 === 'string' ? structuredData.pdfBase64.trim() : '';
    if (!base64) {
        return 'PDF';
    }

    const inferredBytes = Math.floor(base64.length * 3 / 4);
    return toHumanSize(inferredBytes) || 'PDF';
};

const DocumentIcon = () => {
    return (
        <svg viewBox='0 0 24 24' width='20' height='20' aria-hidden='true' focusable='false'>
            <path d='M6 2h8l4 4v16H6z' fill='none' stroke='currentColor' strokeWidth='1.8' />
            <path d='M14 2v4h4' fill='none' stroke='currentColor' strokeWidth='1.8' />
            <path d='M9 11h6M9 14h6M9 17h4' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' />
        </svg>
    );
};

const DownloadIcon = () => {
    return (
        <svg viewBox='0 0 24 24' width='18' height='18' aria-hidden='true' focusable='false'>
            <path d='M12 3v11' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' />
            <path d='M8 11l4 4 4-4' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
            <path d='M5 20h14' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' />
        </svg>
    );
};

const triggerDownload = ({ href, fileName, target = '_self' }) => {
    const link = document.createElement('a');
    link.href = href;
    link.download = fileName;
    link.target = target;
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const downloadPdfWithoutNavigation = async ({ pdfSrc, fileName }) => {
    if (typeof pdfSrc !== 'string' || pdfSrc.trim().length === 0) {
        return;
    }

    if (pdfSrc.startsWith('data:') || pdfSrc.startsWith('blob:')) {
        triggerDownload({ href: pdfSrc, fileName });
        return;
    }

    try {
        const response = await fetch(pdfSrc);
        if (!response.ok) {
            throw new Error('PDF download failed');
        }

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        triggerDownload({ href: objectUrl, fileName });
        URL.revokeObjectURL(objectUrl);
    } catch (error) {
        // Preserve current tab; use new tab as graceful fallback when direct download is blocked.
        triggerDownload({ href: pdfSrc, fileName, target: '_blank' });
    }
};

const PdfMessage = ({ structuredData, labels }) => {
    const pdfSrc = buildPdfSrc(structuredData);
    const title = typeof structuredData?.title === 'string' && structuredData.title.trim().length > 0 ? structuredData.title.trim() : '';
    const explicitFileName = typeof structuredData?.fileName === 'string' && structuredData.fileName.trim().length > 0
        ? structuredData.fileName.trim()
        : '';
    const fileNameFromUrl = getFileNameFromUrl(structuredData?.url);
    const displayName = explicitFileName || title || fileNameFromUrl || labels.attachedDocument;
    const safeName = displayName.toLowerCase().endsWith('.pdf') ? displayName : `${displayName}.pdf`;
    const fallbackName = `${toDownloadName(safeName, 'attached-document')}.pdf`;
    const downloadFileName = safeName || fallbackName;
    const fileSize = inferPdfSize(structuredData);

    const handleDownloadClick = async () => {
        await downloadPdfWithoutNavigation({ pdfSrc, fileName: downloadFileName });
    };

    if (!pdfSrc) {
        return (
            <div className='copilot-chat-message copilot-chat-message--structured assistant'>
                <div className='copilot-chat-media-fallback'>{labels.pdfUnavailable}</div>
            </div>
        );
    }

    return (
        <div className='copilot-chat-message copilot-chat-message--structured assistant'>
            <div className='copilot-chat-pdf-file-card'>
                <div className='copilot-chat-pdf-file-icon'>
                    <DocumentIcon />
                </div>
                <div className='copilot-chat-pdf-file-meta'>
                    <a className='copilot-chat-pdf-file-name' href={pdfSrc} target='_blank' rel='noopener noreferrer'>
                        {displayName}
                    </a>
                    <div className='copilot-chat-pdf-file-size'>{fileSize}</div>
                </div>
                <button
                    type='button'
                    className='copilot-chat-pdf-file-download'
                    onClick={handleDownloadClick}
                    aria-label={`${labels.download} ${displayName}`}
                    title={labels.download}
                >
                    <DownloadIcon />
                </button>
            </div>
        </div>
    );
};

export default PdfMessage;
